'use client';

import { useEffect, useState } from 'react';

import { getPromotionSalesStatusService } from '@/app/(main)/(pages)/busca/[slug]/_services/get-promotion-sales-status.service';
import { getLogger } from '@/logger/logger';

const logger = getLogger('promotion.sales-status');

export interface PromotionSalesStatus {
  isUrgent: boolean;
  isLoading: boolean;
}

export function usePromotionSalesStatus(
  promotionIds: string[],
  enabled: boolean,
): PromotionSalesStatus {
  const [isUrgent, setIsUrgent] = useState(false);
  const [isLoading, setIsLoading] = useState(enabled);

  const promotionIdsKey = promotionIds.join(',');

  useEffect(() => {
    if (!enabled || promotionIdsKey.length === 0) {
      setIsUrgent(false);
      setIsLoading(false);

      return undefined;
    }

    let isCurrent = true;

    setIsLoading(true);

    Promise.all(
      promotionIdsKey.split(',').map((promotionId) =>
        getPromotionSalesStatusService(promotionId).catch((error) => {
          logger.error('Failed to read the promotion sales status', {
            promotion: { event: 'sales_status_failed', promotion_id: promotionId },
            error,
          });

          return { budgetIsCloseToEnd: false, salesLimitIsCloseToEnd: false };
        })),
    ).then((statuses) => {
      if (!isCurrent) {
        return;
      }

      setIsUrgent(statuses.some((status) => status.budgetIsCloseToEnd || status.salesLimitIsCloseToEnd));
      setIsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [enabled, promotionIdsKey]);

  return { isUrgent, isLoading };
}
